import { useAuthContext } from '@/modules/auth/context/AuthProvider';
import { authFetch } from '@/modules/auth/utils/authFetch';
import * as Sentry from '@sentry/browser';
import { trackSubscriptionPurchase } from '@/services/analytics';

export function useSubscriptions() {
  const { user, session } = useAuthContext();

  const ensureValidSession = async () => {
    if (!session?.access_token) {
      throw new Error('No active session found');
    }
    return session;
  };

  const getSubscriptionStatus = async () => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log('Fetching subscription status for user:', user.id);
      const session = await ensureValidSession();

      const response = await authFetch('/api/subscription-status', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Subscription status API error:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Subscription status response:', data);
      return data;
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      Sentry.captureException(error, {
        extra: { 
          action: 'getSubscriptionStatus',
          userId: user?.id,
          userEmail: user?.email 
        }
      });
      throw error;
    }
  };

  const createCheckoutSession = async (priceId, currency = 'USD') => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log('Creating checkout session:', { priceId, currency, userId: user.id });
      const session = await ensureValidSession();

      // Validate inputs
      if (!priceId) {
        throw new Error('Price ID is required');
      }

      if (!['USD', 'EUR', 'GBP'].includes(currency)) {
        console.warn('Invalid currency specified, defaulting to USD:', currency);
        currency = 'USD';
      }

      const response = await authFetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          currency: currency.toLowerCase(), // Stripe expects lowercase
          successUrl: `${window.location.origin}/subscription/success`,
          cancelUrl: `${window.location.origin}/subscription/cancel`
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Checkout session API error:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
          requestData: {
            priceId,
            currency,
            userId: user.id
          }
        });
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Checkout session created successfully:', data);

      // Track the purchase event with the selected currency
      const price = {
        USD: 8,
        EUR: 7,
        GBP: 6
      }[currency];

      trackSubscriptionPurchase(priceId, currency, price);

      return data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      Sentry.captureException(error, {
        extra: { 
          action: 'createCheckoutSession',
          priceId,
          currency,
          userId: user?.id,
          userEmail: user?.email,
          sessionExists: !!user
        }
      });
      throw error;
    }
  };

  /**
   * Starts a cardless 7-day Premium trial in the given currency. Throws with
   * `trialAlreadyUsed` when the account has had a subscription before (409).
   */
  const startTrial = async (priceId, currency = 'USD') => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }
      if (!priceId) {
        throw new Error('Price ID is required');
      }

      const normalizedCurrency = String(currency || 'USD').toUpperCase();
      if (!['USD', 'EUR', 'GBP'].includes(normalizedCurrency)) {
        throw new Error('Invalid currency. Allowed: USD, EUR, GBP');
      }

      console.log('Starting free trial:', { priceId, currency: normalizedCurrency, userId: user.id });
      const session = await ensureValidSession();

      const response = await authFetch('/api/start-trial', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ priceId, currency: normalizedCurrency.toLowerCase() }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(data.error || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.trialAlreadyUsed = response.status === 409 || data.trialAlreadyUsed === true;
        throw error;
      }

      console.log('Free trial started:', data);
      return data;
    } catch (error) {
      console.error('Error starting free trial:', error);
      Sentry.captureException(error, {
        extra: {
          action: 'startTrial',
          priceId,
          userId: user?.id,
          userEmail: user?.email
        }
      });
      throw error;
    }
  };

  const createPortalSession = async () => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log('Creating customer portal session for user:', user.id);
      const session = await ensureValidSession();

      const response = await authFetch('/api/customer-portal', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          returnUrl: window.location.origin
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Portal session API error:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Portal session created:', data);
      return data;
    } catch (error) {
      console.error('Error creating portal session:', error);
      Sentry.captureException(error, {
        extra: { 
          action: 'createPortalSession',
          userId: user?.id,
          userEmail: user?.email 
        }
      });
      throw error;
    }
  };

  // Legacy method for backwards compatibility
  const createCustomerPortalSession = async () => {
    return createPortalSession();
  };

  return {
    getSubscriptionStatus,
    createCheckoutSession,
    startTrial,
    createPortalSession,
    createCustomerPortalSession // Backwards compatibility
  };
}